import { Eye, Send } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'

import QuoteStatusBadge from './QuoteStatusBadge'

export default function QuoteMobileCard({ quote, onView, onSend }) {
  const products = quote.products || []
  const productsWithDesc = products.filter(
    (p) => typeof p.description === 'string' && p.description.trim().length > 0,
  )
  const productsWithoutDesc = products.filter(
    (p) =>
      !p.description ||
      typeof p.description !== 'string' ||
      p.description.trim().length === 0,
  )
  const isPartialDesc =
    products.length > 1 &&
    productsWithDesc.length > 0 &&
    productsWithoutDesc.length > 0

  const product = isPartialDesc
    ? productsWithDesc[0]?.product || '-'
    : products[0]?.product || quote.product || '-'
  const restCount = products.length > 1 ? products.length - 1 : 0
  const descriptions = productsWithDesc.map((p) => p.description.trim())

  if (!descriptions.length && quote.description?.trim()) {
    descriptions.push(quote.description.trim())
  }

  const qty = products.length
    ? products.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0) || '-'
    : (quote.qty ?? '-')
  const expires = quote.validUntil
    ? new Date(quote.validUntil).toLocaleDateString('en-IN')
    : quote.expires || '-'

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {quote.quoteNo || '-'}
          </h3>

          <p className="text-sm text-slate-500">
            {quote.contactName || quote.customer || '-'}
          </p>
        </div>

        <QuoteStatusBadge status={quote.status || 'draft'} />
      </div>

      <div className="mt-4 space-y-2 text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="shrink-0 text-slate-500">Product</span>
          <div className="flex min-w-0 max-w-[65%] items-center justify-end gap-1.5">
            <span
              className="truncate font-medium text-slate-900"
              title={product}
            >
              {product}
            </span>
            {restCount > 0 ? (
              <button
                type="button"
                onClick={() => onView?.(quote)}
                className="inline-flex shrink-0 items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100 cursor-pointer"
              >
                +{restCount} more
              </button>
            ) : null}
          </div>
        </div>

        {descriptions.length > 0 && (
          <div className="flex justify-between items-center gap-2">
            <span className="text-slate-500 shrink-0">Description</span>
            <div className="flex items-center gap-1.5 justify-end max-w-[200px] min-w-0">
              <span className="truncate font-medium text-slate-700 text-xs">
                {descriptions[0]}
              </span>
              {descriptions.length > 1 && (
                <button
                  type="button"
                  onClick={() => onView?.(quote)}
                  className="inline-flex shrink-0 items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-100"
                >
                  +{descriptions.length - 1} more
                </button>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-between">
          <span className="text-slate-500">Qty</span>
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-slate-900">{qty}</span>
            {restCount > 0 ? (
              <span className="inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700">
                +{restCount} more
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex justify-between">
          <span className="text-slate-500">Total</span>
          <span className="font-medium text-slate-900">
            ₹{(quote.grandTotal || 0).toLocaleString('en-IN')}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-slate-500">Expires</span>
          <span className="font-medium text-slate-900">{expires}</span>
        </div>
      </div>

      <div className="mt-5 flex gap-2.5">
        {onSend ? (
          <Button
            type="button"
            onClick={() => onSend(quote)}
            className="flex-1 gap-2 rounded-xl bg-[#0F172A] font-semibold text-white hover:bg-slate-700"
          >
            <Send className="size-4" />
            Send
          </Button>
        ) : null}

        <Button
          type="button"
          variant="outline"
          onClick={() => onView(quote)}
          className="flex-1 gap-2 rounded-xl border-slate-200 font-semibold text-slate-700"
        >
          <Eye className="size-4" />
          View
        </Button>
      </div>
    </div>
  )
}
